import { forwardRef, type SelectHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../lib/utils';

const nativeSelectVariants = cva(
  [
    'flex h-9 w-full appearance-none rounded-md border bg-background px-3 py-2 pr-8 text-sm shadow-2xs',
    'outline-none transition-colors',
    'focus-visible:ring-2 focus-visible:ring-primary/40',
    'disabled:cursor-not-allowed disabled:opacity-50',
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'border-input text-foreground',
        destructive: 'border-destructive focus-visible:ring-destructive/40 text-foreground',
        placeholder: 'border-input text-muted-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface NativeSelectProps
  extends
    SelectHTMLAttributes<HTMLSelectElement>,
    Omit<VariantProps<typeof nativeSelectVariants>, 'variant'> {
  /** Use 'placeholder' when no value is selected so the text renders in muted colour. */
  variant?: 'default' | 'destructive' | 'placeholder';
}

function ChevronDownIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="pointer-events-none text-muted-foreground"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

const NativeSelect = forwardRef<HTMLSelectElement, NativeSelectProps>(
  ({ className, variant = 'default', children, ...props }, ref) => (
    <div className="relative w-full">
      <select
        ref={ref}
        data-slot="native-select"
        className={cn(nativeSelectVariants({ variant }), className)}
        {...props}
      >
        {children}
      </select>
      {/* Indicador alinhado ao topo (início) do campo, não centralizado. */}
      <div className="pointer-events-none absolute right-2.5 top-2 flex items-start">
        <ChevronDownIcon />
      </div>
    </div>
  )
);
NativeSelect.displayName = 'NativeSelect';

export { NativeSelect, nativeSelectVariants };
